import { Entity } from "@common/types/entity";
import {
    buildTemplateEmailBody,
    buildTemplateEmailSubject,
    resolveInvoiceEmailPlaceholders,
} from "@common/helpers/invoice-email-defaults.helper";
import { migrateLegacyInvoiceEmailTemplate } from "@common/helpers/invoice-email-variables";
import {
    buildDefaultEmailBody,
    buildDefaultEmailSubject,
    firstNonEmptyString,
} from "./nativeInvoiceFormHelpers";
import {
    NativeInvoiceFormRawState,
    NativeInvoiceFormState,
} from "./nativeInvoiceFormTypes";

export interface ResolveFormStateContext {
    suggestedInvoiceNumber?: string;
    currentUserName?: string | null;
    currentUserEmail?: string | null;
    defaultEntity?: Entity;
    // Template sessions show the email subject/body with `{{entityName}}` /
    // `{{invoiceNumber}}` / `{{invoiceDate}}` / `{{dueDate}}` chips instead
    // of the concrete values, which only resolve when the template is applied
    // to an invoice.
    isTemplateMode?: boolean;
}

// Resolve the soft-default overrides against the live context: a null override
// keeps deriving from props/entity, a non-null override is the user's explicit
// value. Pure so the override-null derivation can be unit-tested without React.
export function resolveFormState(
    raw: NativeInvoiceFormRawState,
    context: ResolveFormStateContext,
): NativeInvoiceFormState {
    const {
        emailSubjectOverride,
        emailMessageOverride,
        invoiceNumberOverride,
        replyToNameOverride,
        replyToEmailOverride,
        fromEmailOverride,
        from,
        ...rest
    } = raw;

    const invoiceNumber =
        invoiceNumberOverride ?? context.suggestedInvoiceNumber ?? "";
    const replyToName = replyToNameOverride ?? context.currentUserName ?? "";
    const replyToEmail = replyToEmailOverride ?? context.currentUserEmail ?? "";
    const fromEmail =
        fromEmailOverride ??
        firstNonEmptyString(
            context.defaultEntity?.user?.email,
            context.currentUserEmail,
        );

    const automaticSubject = context.isTemplateMode
        ? buildTemplateEmailSubject()
        : buildDefaultEmailSubject({
              businessName: from.businessName,
              invoiceNumber,
          });
    const automaticMessage = context.isTemplateMode
        ? buildTemplateEmailBody({ senderName: context.currentUserName })
        : buildDefaultEmailBody({
              invoiceNumber,
              senderName: context.currentUserName,
          });

    // Invoice sessions resolve any `{{entityName}}` / `{{invoiceNumber}}` /
    // `{{invoiceDate}}` / `{{dueDate}}` tokens (and the legacy bracket
    // spellings) carried over from an applied template; template sessions
    // keep the tokens so the editor can chip them.
    const resolvePlaceholders = (text: string) =>
        context.isTemplateMode
            ? migrateLegacyInvoiceEmailTemplate(text)
            : resolveInvoiceEmailPlaceholders({
                  text,
                  businessName: from.businessName,
                  invoiceNumber,
                  invoiceDate: rest.invoiceDate,
                  dueDate: rest.dueDate,
              });

    return {
        ...rest,
        from: { ...from, email: fromEmail },
        invoiceNumber,
        replyToName,
        replyToEmail,
        emailSubject: resolvePlaceholders(
            emailSubjectOverride ?? automaticSubject,
        ).slice(0, 255),
        emailMessage: resolvePlaceholders(
            emailMessageOverride ?? automaticMessage,
        ),
        isEmailSubjectAutomatic: emailSubjectOverride === null,
        isEmailMessageAutomatic: emailMessageOverride === null,
    };
}

// Strip the resolved-only fields off a resolved state to recover the shared raw
// fields. Destructuring (not a hand-copied field list) keeps the raw/resolved
// split a single source of truth, so a new form field can't be silently
// dropped on the way back to raw state.
function toRawFields(
    state: NativeInvoiceFormState,
): Omit<
    NativeInvoiceFormState,
    | "emailSubject"
    | "emailMessage"
    | "isEmailSubjectAutomatic"
    | "isEmailMessageAutomatic"
    | "invoiceNumber"
    | "replyToName"
    | "replyToEmail"
> {
    const {
        emailSubject,
        emailMessage,
        isEmailSubjectAutomatic,
        isEmailMessageAutomatic,
        invoiceNumber,
        replyToName,
        replyToEmail,
        ...rawFields
    } = state;
    return rawFields;
}

// Map a resolved-state update back onto raw overrides. A soft-default field
// becomes an override only when its resolved value actually changed (empty
// invoice number clears the override so a new suggestion can apply), so the
// public setState can speak in resolved `NativeInvoiceFormState`.
export function applyResolvedFormState({
    prevRaw,
    prev,
    next,
}: {
    prevRaw: NativeInvoiceFormRawState;
    prev: NativeInvoiceFormState;
    next: NativeInvoiceFormState;
}): NativeInvoiceFormRawState {
    let invoiceNumberOverride = prevRaw.invoiceNumberOverride;
    if (next.invoiceNumber !== prev.invoiceNumber) {
        invoiceNumberOverride =
            next.invoiceNumber === "" ? null : next.invoiceNumber;
    }
    const replyToNameOverride =
        next.replyToName !== prev.replyToName
            ? next.replyToName
            : prevRaw.replyToNameOverride;
    const replyToEmailOverride =
        next.replyToEmail !== prev.replyToEmail
            ? next.replyToEmail
            : prevRaw.replyToEmailOverride;
    // Freeze derived from-email when the user edits the email, so late
    // currentUser/entity changes cannot fight an explicit choice.
    const fromEmailOverride =
        next.from.email !== prev.from.email
            ? next.from.email
            : prevRaw.fromEmailOverride;
    const emailSubjectOverride =
        next.emailSubject !== prev.emailSubject
            ? next.emailSubject
            : prevRaw.emailSubjectOverride;
    const emailMessageOverride =
        next.emailMessage !== prev.emailMessage
            ? next.emailMessage
            : prevRaw.emailMessageOverride;

    return {
        ...toRawFields(next),
        invoiceNumberOverride,
        replyToNameOverride,
        replyToEmailOverride,
        fromEmailOverride,
        emailSubjectOverride,
        emailMessageOverride,
    };
}
